import React, { useState, useEffect, useRef } from 'react';
import { Redirect } from "react-router-dom";
import { Layout, Typography, Form, Input, Button, notification } from 'antd';
import { PasswordInput } from "antd-password-input-strength";
import _auth from '@netuno/auth-client';
import _service from '@netuno/service-client';

import Core from '../../common/Core';

import { FormattedMessage, useIntl } from 'react-intl';

import AddressFields from '../../components/AddressFields';
import ProfileSelect from '../../components/ProfileSelect';

import './index.less';

const { Title } = Typography;
const { Content, Sider } = Layout;

const langContext = 'register';

export default function Register(props) {

  const [ready, setReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (_auth.isLogged()) {
      window.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  });

  const intl = useIntl();

  function onFinish(values) {
    setSubmitting(true);
    const { username, password, mail, name, postal_code, address_extra, profile_uid, nif } = values;
    _service({
      method: 'POST',
      url: 'client',
      data: {
        name,
        username,
        password,
        mail,
        postal_code,
        address_extra,
        nif,
        profile_uid,
        language_code: Core.currentLanguage()
      },
      success: (response) => {
        if (response.json.result) {
          notification["success"]({
            message: 'Conta Criada',
            description: 'A conta foi criada com sucesso, pode iniciar sessão.',
          });
          setSubmitting(false);
          setReady(true);
        } else {
          notification["warning"]({
            message: 'Utilizador existente',
            description: response.json.error,
          });
          setSubmitting(false);
        }
      },
      fail: () => {
        setSubmitting(false);
        notification["error"]({
          message: 'Erro na Criação de Conta',
          description: 'Não foi possível criar a conta, contacte-nos através do chat de suporte.',
        });
      }
    });
  }

  function onFinishFailed(errorInfo) {
    console.log('Failed:', errorInfo);
  }

  if (_auth.isLogged()) {
    return <Redirect to="/reserved-area" />;
  }
  else if (ready) {
    return <Redirect to="/login" />;
  } else {
    return (
      <Layout className="content-body">
        <Content className="register-container">
          <div className="content-title">
            <Title><FormattedMessage id={`${langContext}.title`} /></Title>
          </div>
          <div className="content-body">
            <p>{intl.formatMessage({ id: 'register.content-body' })}</p>
            <Form
              form={form}
              layout="vertical"
              name="basic"
              initialValues={{ remember: true }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
            >
              <Form.Item
                label={intl.formatMessage({ id: 'register.fields.name.title' })}
                name="name"
                rules={[
                  { required: true, message: 'Insira o seu nome.' },
                  { type: 'string', message: 'Nome inválido, apenas letras minúsculas e maiúsculas.', pattern: "^[a-zA-ZàáâäãåąčćęèéêëėįìíîïłńòóôöõøùúûüųūÿýżźñçčšžÀÁÂÄÃÅĄĆČĖĘÈÉÊËÌÍÎÏĮŁŃÒÓÔÖÕØÙÚÛÜŲŪŸÝŻŹÑßÇŒÆČŠŽ∂ð ,.'-]+$" }
                ]}
              >
                <Input placeholder={intl.formatMessage({ id: 'register.fields.name.placeholder' })} disabled={submitting} maxLength={25} />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({ id: 'register.fields.username.title' })}
                name="username"
                rules={[
                  { required: true, message: 'Insira o seu nome de utilizador.' },
                  { type: 'string', message: 'Utilizador inválido, apenas letras minúsculas e números.', pattern: '^[a-z0-9]{1,25}$' }
                ]}
              >
                <Input placeholder={intl.formatMessage({ id: 'register.fields.username.placeholder' })} disabled={submitting} maxLength={25} />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({ id: 'register.fields.email.title' })}
                name="mail"
                rules={[
                  { type: 'email', message: 'O e-mail inserido não é válido.' },
                  { required: true, message: 'Insira o e-mail.' }
                ]}
              >
                <Input placeholder={intl.formatMessage({ id: 'register.fields.email.placeholder' })} disabled={submitting} maxLength={250} />
              </Form.Item>
              <AddressFields form={form} />
              <Form.Item
                name="profile_uid"
                label={intl.formatMessage({ id: 'register.fields.profile' })}
                rules={[
                  { required: true }
                ]}
              >
                <ProfileSelect />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({ id: 'register.fields.nif.title' })}
                name="nif"
                rules={[
                  { required: true, message: 'Insira o NIF.' }
                ]}
              >
                <Input placeholder={intl.formatMessage({ id: 'register.fields.nif.placeholder' })} disabled={submitting} maxLength={250} />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({ id: 'register.fields.password.title' })}
                name="password"
                rules={[
                  { required: true, message: 'Insira a palavra-passe.' },
                  { type: 'string', message: 'Palavra-Passe deverá ter entre 8 a 25 caracteres.', min: 8, max: 25 },
                ]}
              >
                <PasswordInput placeholder={intl.formatMessage({ id: 'register.fields.password.placeholder' })} disabled={submitting} maxLength={25} />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({ id: 'register.fields.confirm-password.title' })}
                name="password_confirm"
                rules={[
                  { required: true, message: 'Insira a confirmação da palavra-passe.' },
                  { type: 'string', message: 'Palavra-Passe deverá ter entre 8 a 25 caracteres.', min: 8, max: 25 },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('password') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject('As palavras-passes não são iguais.');
                    },
                  })
                ]}
              >
                <Input.Password placeholder={intl.formatMessage({ id: 'register.fields.confirm-password.placeholder' })}
                  disabled={submitting} maxLength={25} />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting}>
                  <FormattedMessage id={`${langContext}.button.create`} />
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Content>
        <Sider width={'50%'} style = {{'position':'relative'}}>
          <span class="helper" /><img style = {{'position':'fixed', 'top':'50%', 'transform':'translate(0%,-50%)', 'right':0, 'width':'50%'}} alt="sider-register" src={"/images/Background_home.svg"} />
        </Sider>
      </Layout>
    );
  }

}
